import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { LinkItem, MediaItem } from 'Interfaces/general.interface';
import ComponentImage from 'GeneralComponents/ComponentImage';
import Consts from "Interfaces/Consts";
import CustomLinkButton from "GeneralComponents/CustomLinkButton";
import LimitedContent from 'GeneralComponents/LimitedContent';
import { decodeHtmlEntities } from '../../../Interfaces/utils/Utils';

type ContentIcon = {
    icon: string;
    title: string;
    info: string;
}

const ContentCard = ({ children }: { children: JSX.Element | JSX.Element[] }) => {
    return <article className="content-card pallete-border-color mt-4"> {children} </article>
}

const Title = ({ title }: { title: string }) => {
    return title && <h3 className='content-title clamp'>{title}</h3>;
}

const SubTitle = ({ text }: { text: string }) => {
    return text && <p className="content-subtitle clamp" dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(text) }}></p>;
}

const FilterIcon = ({ icon, info, title }: { icon: string; title: string; info: string; }) => {
    return info && <article className='icon-wrapper px-3 d-flex text-start col-12 col-lg-auto'>
        {icon && <i aria-hidden="true" className={`${icon} icon txt-pallete-color px-2`}></i>}
        <div className="d-block">
            <span className='icon-title px-1'>{title}:</span>
            <span className='icon-info mb-0'>{info}</span>
        </div>
    </article>
}

const Image = ({ image, mobileImage }: { image: MediaItem; mobileImage: MediaItem }) => {

    return image.url && <ComponentImage
        image={image}
        imageClass="content-img"
        mobileImage={mobileImage}
        mobileImageClass="content-img"
        wrapperDivClass={'mb-0'}
        cropAlias="squareImage"
    />;
}

const Icon = ({ icon }: { icon: string }) => {
    return icon && <div className="content-img d-flex align-items-center justify-content-center">
        <i aria-hidden="true" className={`${icon} txt-pallete-color`}></i>
    </div>
}

const LinkBtn = ({ link, linkTarget }: { link: LinkItem, linkTarget: string }) => {

    const dir = getComputedStyle(document.documentElement).direction;

    return link.url && < CustomLinkButton
        title={link.title}
        pageUrl={''}
        link={link}
        isBtn={true}
        btnClass={'content-link-btn btn mst-btn btn-outline-primary'}
        linkClass={""}
        dir={dir}
        linkIcon={true}
        charLimit={45}
        linkTarget={linkTarget}
    />;
}

const HeaderWithChildren = ({children}: PropsWithChildren) => {
    return <div className="title_contianer d-flex mb-2 gap-2 flex-lg-row flex-column"> {children} </div>
}

const HeaderTextsWithChildren = ({ children, className }: PropsWithChildren<{ className?: string }>) => {
    return <div className={`d-flex flex-column justify-content-between ${className ?? ''}`}> {children} </div>;
}

const DescriptionWithChildren = ({ content, contentId, children, limitContent, hasChildren }: { hasChildren: boolean; limitContent: boolean; children?: JSX.Element; content: string; contentId: string; }) => {

    const [isContentVisible, setIsContentVisible] = useState(!limitContent);
    const [hasShowMoreBtn, setHasShowMoreBtn] = useState(limitContent);
    const contentRef = useRef<HTMLDivElement>();
    const btnElementId = `${contentId}-readmore-btn`;
    const contentElementId = `${contentId}-content-description`;

    const toggleReadMore = () => setIsContentVisible(prev => !prev);

    useEffect(() => {
        if (contentRef.current) {
            const isContentClamped = contentRef.current.clientHeight !== contentRef.current.scrollHeight;
            setHasShowMoreBtn(limitContent && isContentClamped)
        }
    }, []);

    return (
        <div className="content-description">

            <div ref={contentRef} className={`${content ? '' : 'd-none'} ${isContentVisible ? '' :'clamp line-clamp-3'}`}
                aria-expanded={isContentVisible}
                id={contentElementId}>
                {
                    isContentVisible ?
                        <div dangerouslySetInnerHTML={{ __html: content }} /> :
                        < LimitedContent htmlContent={content} />
                }
            </div>

            { (hasChildren || hasShowMoreBtn) && < div className="my-3 footer-line" /> }

            <div className="d-flex align-items-center">

                {
                    content && hasShowMoreBtn &&
                    <a className="read-more-btn pe-2"
                        aria-controls={contentElementId}
                        id={btnElementId}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key == 'Enter' ? toggleReadMore(): null}
                        onClick={toggleReadMore}>

                        {isContentVisible ? Consts.close : Consts.readMore}
                        <i aria-hidden="true" className={`fa fa-angle-${isContentVisible? 'up':'down'} px-1`}></i>
                    </a>
                }

                <div className="flex-fill text-end">
                    {children}
                </div>

            </div>

        </div>
    )
}

const FilterIconsWrapper = ({ icons }: { icons: ContentIcon[] }) => {
    return <div className="icons-section d-lg-flex flex-wrap row mb-3 gap-1">
        {icons.map((icon, idx) => <FilterIcon key={idx} {...icon} />) }
    </div>
}

HeaderWithChildren.Title = Title;
HeaderWithChildren.SubTitle = SubTitle;
HeaderWithChildren.Image = Image;
HeaderWithChildren.Icon = Icon;
ContentCard.Header = HeaderWithChildren;
ContentCard.HeaderTextsWrapper = HeaderTextsWithChildren;
ContentCard.FilterIcons = FilterIconsWrapper;
ContentCard.Description = DescriptionWithChildren;
ContentCard.LinkBtn = LinkBtn;

export default ContentCard;